// Load 1x in theme file and this script will power all instances of the site discount bar (currently in site header & cart drawer)
(function () {
    const CART_PROGRESS_SELECTOR = '[data-cart-progress]';
    const CART_PROGRESS_STATE_SELECTOR = '[data-cart-progress-state]';
    const CART_PROGRESS_VIEW_SELECTOR = '[data-cart-progress-view]';

    function dispatchStateEvent(instanceId, state) {
        try {
            window.dispatchEvent(
                new CustomEvent('cartprogress:statechange', {
                    detail: {
                        instanceId,
                        state
                    }
                })
            );
        } catch (error) {
            console.warn('cartprogress:statechange dispatch failed', error);
        }
    }

    function toSafeNumber(value) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    function toFiniteNumber(value, fallback) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    }

    function clampPercentage(rawValue, fallback = 0) {
        if (rawValue === undefined || rawValue === null || rawValue === '') {
            return fallback;
        }
        const parsed = toFiniteNumber(rawValue, fallback);
        if (!Number.isFinite(parsed)) return fallback;
        return Math.min(Math.max(parsed, 0), 100);
    }

    const CartProgressCore = (() => {
        const SUBSCRIPTION_ITEM_BOOST = 2;
        const state = new Map();
        const listeners = new Map();

        function normalizeCart(rawCart) {
            const cart = rawCart || {};
            const items = Array.isArray(cart.items) ? cart.items : [];

            return {
                item_count: toSafeNumber(cart.item_count),
                items: items.map((item) => ({
                    quantity: toSafeNumber(item.quantity),
                    original_price: toSafeNumber(item.original_price),
                    bundle_items_total: toSafeNumber(item.bundle_items_total || item.properties?.bundle_items_total),
                    has_selling_plan: Boolean(
                        item.has_selling_plan ||
                        item.selling_plan_allocation ||
                        (Array.isArray(item.selling_plan_allocations) && item.selling_plan_allocations.length > 0)
                    )
                }))
            };
        }

        function computeMetrics(cart) {
            let itemUnits = 0;
            let hasSubscription = false;

            cart.items.forEach((item) => {
                if (item.original_price <= 0) return;
                if (item.has_selling_plan) {
                    hasSubscription = true;
                }
                const bundleCount = item.bundle_items_total > 0 ? item.bundle_items_total : 1;
                itemUnits += bundleCount * item.quantity;
            });

            // A/B Test: Capture actual item count before subscription boost
            const actualItemCount = itemUnits;

            if (hasSubscription) {
                itemUnits += SUBSCRIPTION_ITEM_BOOST;
            }

            return {
                itemCount: cart.item_count,
                itemUnits,
                actualItemCount,
                hasSubscription
            };
        }

        function notify(instanceId) {
            const currentState = getState(instanceId);
            const instanceListeners = listeners.get(instanceId);
            if (!currentState || !instanceListeners) return;

            instanceListeners.forEach((listener) => {
                try {
                    listener(currentState);
                } catch (error) {
                    console.error('CartProgressCore listener failed', error);
                }
            });
        }

        function initializeState(instanceId, cart) {
            const normalizedCart = normalizeCart(cart);
            const metrics = computeMetrics(normalizedCart);
            state.set(instanceId, { cart: normalizedCart, metrics });
            notify(instanceId);
        }

        function updateCart(instanceId, cart) {
            if (!state.has(instanceId)) {
                console.warn('CartProgressCore: update before initialization', instanceId);
                return;
            }

            const normalizedCart = normalizeCart(cart);
            const metrics = computeMetrics(normalizedCart);
            state.set(instanceId, { cart: normalizedCart, metrics });
            notify(instanceId);
        }

        function getState(instanceId) {
            return state.get(instanceId) || null;
        }

        function subscribe(instanceId, listener) {
            if (!listeners.has(instanceId)) {
                listeners.set(instanceId, new Set());
            }

            const instanceListeners = listeners.get(instanceId);
            instanceListeners.add(listener);

            return () => {
                instanceListeners.delete(listener);
                if (instanceListeners.size === 0) {
                    listeners.delete(instanceId);
                }
            };
        }

        function destroy(instanceId) {
            state.delete(instanceId);
            listeners.delete(instanceId);
        }

        return {
            initializeState,
            updateCart,
            subscribe,
            getState,
            destroy,
            normalizeCart,
            computeMetrics
        };
    })();

    function parseStagesFromMarkup(view) {
        const stageNodes = view.querySelectorAll('[data-cart-progress-stage]');
        if (!stageNodes.length) {
            console.warn('no stageNodes found')
            return []
        }

        const stages = [];

        stageNodes.forEach((node) => {
            const threshold = toFiniteNumber(node.dataset.threshold ?? node.dataset.cartProgressThreshold, undefined);
            if (!Number.isFinite(threshold)) {
                console.warn('Ignoring stage with invalid threshold', node);
                return;
            }

            const widthRaw = node.dataset.width ?? node.dataset.cartProgressWidth;
            const width = widthRaw === undefined ? null : clampPercentage(widthRaw, 0);

            const primaryNode = node.querySelector('[data-role="primary"]');
            const secondaryNode = node.querySelector('[data-role="secondary"]');

            stages.push({
                threshold,
                width,
                primaryHTML: primaryNode ? primaryNode.innerHTML.trim() : '',
                secondaryHTML: secondaryNode ? secondaryNode.innerHTML.trim() : '',
                isAbTest: node.dataset.abTest === 'reduced-ua-pricing'
            });
        });

        if (!stages.length) {
            console.warn('no stages found')
            return [];
        }

        stages.sort((a, b) => a.threshold - b.threshold);
        return stages;
    }

    function parseMilestonesFromMarkup(view) {
        const milestones = [];
        view.querySelectorAll('[data-cart-progress-milestone]').forEach((node) => {
            const threshold = toFiniteNumber(node.dataset.threshold ?? node.dataset.cartProgressThreshold, undefined);
            if (!Number.isFinite(threshold)) {
                console.warn('Ignoring milestone with invalid threshold', node);
                return;
            }
            milestones.push({ threshold, element: node });
        });
        return milestones;
    }

    function setNodeContent(node, html) {
        if (!node) return;

        const hasContent = typeof html === 'string' && html.length > 0;
        if (hasContent) {
            node.innerHTML = html;
            node.classList.remove('is-empty');
        } else {
            node.innerHTML = '';
            node.classList.add('is-empty');
        }
    }

    function findStage(stages, count, actualItemCount, cart) {
        if (!stages.length) return null;

        // A/B Test: Check if any item has test variant property
        const hasTestVariantProperty = cart?.items?.some(item => {
            const hasProperty = item.properties?._reduced_ua_pricing_test_variant === "true" ||
                               item.properties?._reduced_ua_pricing_test_variant === true;
            return hasProperty;
        }) || false;

        // A/B test applies when flag is true AND item has property AND actual items (before subscription boost) is 1-2
        const isReducedUATest = window.reducedUAPricing === true;
        const abTestApplies = isReducedUATest && hasTestVariantProperty && actualItemCount >= 1 && actualItemCount <= 2;

        let active = stages[0];
        for (let index = 0; index < stages.length; index += 1) {
            const stage = stages[index];

            // Skip A/B test stages if A/B test doesn't apply
            if (stage.isAbTest && !abTestApplies) continue;

            // Skip non-A/B test stages at same threshold if A/B test applies
            if (abTestApplies && !stage.isAbTest && stages.some(s => s.isAbTest && s.threshold === stage.threshold)) continue;

            if (count >= stage.threshold) {
                active = stage;
            } else {
                break;
            }
        }

        return active;
    }

    let instanceCounter = 0;

    class CartProgressApp {
        constructor(container) {
            this.container = container;
            this.instanceId = container.dataset.cartProgressInstance || `cart-progress-${++instanceCounter}`;
            this.container.dataset.cartProgressInstance = this.instanceId;
            this.initialized = false;
            this.unsubscribe = null;
            this.view = null;
            this.dom = null;
            this.stages = [];
            this.milestones = [];
            this.milestonePositions = new Map();
            this.resizeObserver = null;
            this.measurementCache = null;

            const stateNode = container.querySelector(CART_PROGRESS_STATE_SELECTOR);
            if (!stateNode) {
                console.warn('CartProgressApp: missing state payload');
                return;
            }

            const initialCart = parseJSON(stateNode, 'state payload');
            if (!initialCart) {
                return;
            }
            stateNode.remove();

            const view = container.querySelector(CART_PROGRESS_VIEW_SELECTOR) || container;
            if (!view) {
                console.warn('CartProgressApp: missing view element');
                return;
            }

            this.stages = parseStagesFromMarkup(view);
            this.milestones = parseMilestonesFromMarkup(view);
            this.dom = {
                primary: view.querySelector('[data-cart-progress-target="primary"]'),
                secondary: view.querySelector('[data-cart-progress-target="secondary"]'),
                barFill: view.querySelector('[data-cart-progress-target="bar-fill"]'),
                barTrack: view.querySelector('.cart-progress__bar-track')
            };
            this.view = view;

            CartProgressCore.initializeState(this.instanceId, initialCart);

            this.unsubscribe = CartProgressCore.subscribe(this.instanceId, (state) => {
                this.render(state);
                dispatchStateEvent(this.instanceId, state);
            });

            const initialState = CartProgressCore.getState(this.instanceId);
            if (initialState) {
                this.render(initialState);
                dispatchStateEvent(this.instanceId, initialState);
            }

            this.initialized = true;
        }



        render(state) {
            const metrics = state?.metrics;
            if (!metrics) return;

            const count = metrics.itemUnits || 0;
            const actualItemCount = metrics.actualItemCount || 0;

            if (this.view) {
                this.view.dataset.cartProgressCount = String(count);
            }

            const stage = findStage(this.stages, count, actualItemCount, state.cart) || null;
            const width = stage && Number.isFinite(stage.width) ? stage.width : 0;

            if (this.dom?.barFill) {
                this.dom.barFill.style.width = `${width}%`;
            }

            setNodeContent(this.dom?.primary, stage?.primaryHTML || '');
            setNodeContent(this.dom?.secondary, stage?.secondaryHTML || '');

            if (this.milestones.length) {
                // A/B Test: Check if 25% milestone should be active
                const isReducedUATest = window.reducedUAPricing === true;
                const abTestApplies = isReducedUATest && actualItemCount >= 1 && actualItemCount <= 2;

                this.milestones.forEach(({ element, threshold }) => {
                    if (!element) return;
                    // Milestone 1 (first milestone) is always achieved
                    let isActive = threshold === 1 ? true : count >= threshold;
                    
                    // A/B Test: 25% milestone (threshold 5) is active when A/B test applies
                    if (threshold === 5 && abTestApplies) {
                        isActive = true;
                    }
                    
                    element.dataset.isActive = isActive
                });
            }
        }

        destroy() {
            if (this.unsubscribe) {
                this.unsubscribe();
            }

            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver = null;
            }

            CartProgressCore.destroy(this.instanceId);
            delete this.container.dataset.cartProgressInstance;

            this.view = null;
            this.dom = null;
            this.stages = [];
            this.milestones = [];
            this.milestonePositions.clear();
            this.measurementCache = null;
        }
    }

    function parseJSON(node, label) {
        try {
            const raw = (node.textContent || '{}').trim();
            if (!raw) return {};
            return JSON.parse(raw);
        } catch (error) {
            console.error(`Failed to parse ${label}`, error);
            return null;
        }
    }

    const CartProgressManager = {
        apps: new Map(),

        hydrate(scope = document) {
            scope.querySelectorAll(CART_PROGRESS_SELECTOR).forEach((container) => {
                if (container.dataset.cartProgressInitialized === 'true') {
                    return;
                }

                const app = new CartProgressApp(container);
                if (!app.initialized) {
                    app.destroy();
                    return;
                }

                container.dataset.cartProgressInitialized = 'true';
                CartProgressManager.apps.set(app.instanceId, app);
            });
        },

        destroyWithin(scope) {
            scope.querySelectorAll(CART_PROGRESS_SELECTOR).forEach((container) => {
                const instanceId = container.dataset.cartProgressInstance;
                if (instanceId) {
                    CartProgressManager.destroyInstance(instanceId);
                }
            });
        },

        destroyInstance(instanceId) {
            const app = CartProgressManager.apps.get(instanceId);
            if (!app) return;
            app.destroy();
            CartProgressManager.apps.delete(instanceId);
        },

        updateAll(cart) {
            CartProgressManager.apps.forEach((app) => {
                CartProgressCore.updateCart(app.instanceId, cart);
            });
        }
    };

    function ready(callback) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', callback, { once: true });
            return;
        }
        callback();
    }

    ready(() => CartProgressManager.hydrate(document));

    document.addEventListener('shopify:section:load', (event) => {
        if (event?.target) {
            CartProgressManager.hydrate(event.target);
        }
    });

    document.addEventListener('shopify:section:unload', (event) => {
        if (event?.target) {
            CartProgressManager.destroyWithin(event.target);
        }
    });

    const facade = window.CartProgress || {};
    facade.hydrate = (scope) => CartProgressManager.hydrate(scope || document);
    facade.destroyWithin = (scope) => CartProgressManager.destroyWithin(scope || document);
    facade.updateAll = (cart) => CartProgressManager.updateAll(cart);
    facade.getState = (instanceId) => CartProgressCore.getState(instanceId);
    facade.instances = CartProgressManager.apps;
    facade.core = CartProgressCore;
    window.CartProgress = facade;
})();